import React, { Fragment } from 'react';
import { TrendingUp, TrendingDown, Sparkles, RefreshCw, Edit3 } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Separator } from '../components/ui/separator';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, ResponsiveContainer } from 'recharts';
import { articles, dashboardKPIs, coverageByTier, sentimentOverTime, topSources, spokespersonMentions, executiveSummary, tierLabels, sentimentLabels } from '../lib/data';
function getSentimentColor(sentiment: string) {
  switch (sentiment) {
    case 'positive':
      return 'bg-sentiment-positive';
    case 'neutral':
      return 'bg-sentiment-neutral';
    case 'negative':
      return 'bg-sentiment-negative';
    default:
      return 'bg-muted';
  }
}
function getTierColor(tier: number | string) {
  switch (tier) {
    case 1:
      return 'bg-tier-1 text-white';
    case 2:
      return 'bg-tier-2 text-white';
    case 3:
      return 'bg-tier-3 text-white';
    case 'specialized':
      return 'bg-tier-special text-white';
    default:
      return 'bg-muted';
  }
}
function KPI({
  label,
  value,
  trend,
  accent = false
}: {
  label: string;
  value: string | number;
  trend?: number;
  accent?: boolean;
}) {
  return <div className="flex flex-col gap-2" data-id="element-797">
      <div className="text-sm text-muted-foreground" data-id="element-798">{label}</div>
      <div className={`text-3xl md:text-4xl font-semibold leading-none ${accent ? 'text-accent' : 'text-foreground'}`} style={{
      fontVariantNumeric: 'tabular-nums'
    }} data-id="element-799">
        {value}
      </div>
      {trend !== undefined && <div className="flex items-center gap-1.5 text-sm" data-id="element-800">
          {trend > 0 ? <TrendingUp className="h-3.5 w-3.5 text-sentiment-positive" data-id="element-801" /> : <TrendingDown className="h-3.5 w-3.5 text-sentiment-negative" data-id="element-802" />}
          <span className={trend > 0 ? 'text-sentiment-positive' : 'text-sentiment-negative'} data-id="element-803">
            {trend > 0 ? '+' : ''}
            {trend}%
          </span>
          <span className="text-muted-foreground" data-id="element-804">vs prec.</span>
        </div>}
    </div>;
}
export function Dashboard() {
  const topArticles = articles.filter(a => a.status === 'approved').slice(0, 5);
  return <div className="p-6 max-w-[1400px] mx-auto space-y-8" data-id="element-805">
      {/* Page header */}
      <header data-id="element-806">
        <h1 className="text-2xl md:text-3xl font-semibold tracking-tight" data-id="element-807">
          Dashboard
        </h1>
      </header>

      {/* KPI strip */}
      <section className="bg-card border border-border rounded-lg p-6 grid grid-cols-2 lg:grid-cols-4 gap-8 md:gap-10" data-id="element-808">
        <KPI label="Articoli totali" value={dashboardKPIs.totalArticles.value} trend={dashboardKPIs.totalArticles.trend} data-id="element-809" />
        <KPI label="Score medio" value={dashboardKPIs.avgRelevanceScore.value.toFixed(1)} accent data-id="element-810" />
        <KPI label="AVE totale" value={`€${(dashboardKPIs.totalAVE.value / 1000).toFixed(0)}k`} trend={dashboardKPIs.totalAVE.trend} data-id="element-811" />
        <div className="flex flex-col gap-2" data-id="element-812">
          <div className="text-sm text-muted-foreground" data-id="element-813">Sentiment</div>
          <div className="flex items-baseline gap-2 leading-none" data-id="element-814">
            <span className="text-2xl md:text-3xl font-semibold text-sentiment-positive" style={{
            fontVariantNumeric: 'tabular-nums'
          }} data-id="element-815">
              {dashboardKPIs.sentimentMix.positive}
            </span>
            <span className="text-sm text-muted-foreground" data-id="element-816">/</span>
            <span className="text-xl text-muted-foreground" style={{
            fontVariantNumeric: 'tabular-nums'
          }} data-id="element-817">
              {dashboardKPIs.sentimentMix.neutral}
            </span>
            <span className="text-sm text-muted-foreground" data-id="element-818">/</span>
            <span className="text-xl text-sentiment-negative" style={{
            fontVariantNumeric: 'tabular-nums'
          }} data-id="element-819">
              {dashboardKPIs.sentimentMix.negative}
            </span>
          </div>
          <div className="text-sm text-muted-foreground" data-id="element-820">
            Positivi / neutri / negativi
          </div>
        </div>
      </section>

      {/* Executive Summary */}
      <section className="bg-card border border-border rounded-lg p-6" data-id="element-821">
        <div className="border-l-2 border-accent pl-5 md:pl-6" data-id="element-822">
          <div className="flex items-center justify-between mb-3" data-id="element-823">
            <div className="flex items-center gap-2 text-sm text-muted-foreground" data-id="element-824">
              <Sparkles className="h-3.5 w-3.5 text-accent" data-id="element-825" />
              <span data-id="element-826">Riepilogo AI</span>
            </div>
            <div className="flex items-center gap-1" data-id="element-827">
              <Button variant="ghost" size="sm" className="h-7 px-2 text-xs text-muted-foreground" data-id="element-828">
                <RefreshCw className="h-3 w-3 md:mr-1.5" data-id="element-829" />
                <span className="hidden md:inline" data-id="element-830">Rigenera</span>
              </Button>
              <Button variant="ghost" size="sm" className="h-7 px-2 text-xs text-muted-foreground" data-id="element-831">
                <Edit3 className="h-3 w-3 md:mr-1.5" data-id="element-832" />
                <span className="hidden md:inline" data-id="element-833">Modifica</span>
              </Button>
            </div>
          </div>
          <p className="text-base leading-relaxed text-foreground" data-id="element-834">
            {executiveSummary}
          </p>
        </div>
      </section>

      {/* Main content: articles + analytics */}
      <section className="bg-card border border-border rounded-lg p-6 grid grid-cols-1 lg:grid-cols-3 gap-10 md:gap-12" data-id="element-835">
        {/* Left: Top articles */}
        <div className="lg:col-span-2" data-id="element-836">
          <div className="flex items-baseline justify-between mb-5" data-id="element-837">
            <h2 className="text-lg font-semibold" data-id="element-838">Top articoli</h2>
            <Button variant="link" size="sm" className="text-xs text-muted-foreground h-auto p-0" data-id="element-839">
              Vedi tutti
            </Button>
          </div>

          <div className="space-y-1" data-id="element-840">
            {topArticles.map((article, i) => <Fragment key={article.id} data-id="element-841">
                {i > 0 && <Separator data-id="element-842" />}
                <button className="w-full text-left py-4 md:py-5 group hover:bg-muted/30 -mx-3 px-3 rounded-md transition-colors" data-id="element-843">
                  <div className="flex items-start gap-4" data-id="element-844">
                    <img src={article.sourceLogo} alt={article.source} className="w-9 h-9 rounded flex-shrink-0 mt-0.5" data-id="element-845" />

                    <div className="flex-1 min-w-0" data-id="element-846">
                      <div className="flex flex-wrap items-center gap-2 mb-1.5 text-xs text-muted-foreground" data-id="element-847">
                        <Badge className={`text-[10px] px-1.5 py-0 border-0 ${getTierColor(article.tier)}`} data-id="element-848">
                          {tierLabels[article.tier]}
                        </Badge>
                        <span data-id="element-849">{article.source}</span>
                        <span data-id="element-850">·</span>
                        <span data-id="element-851">
                          {new Date(article.date).toLocaleDateString('it-IT', {
                        day: 'numeric',
                        month: 'short'
                      })}
                        </span>
                      </div>

                      <h3 className="font-medium text-sm md:text-base leading-snug mb-2 line-clamp-2" data-id="element-852">
                        {article.title}
                      </h3>

                      <div className="flex items-center gap-5 text-sm" data-id="element-853">
                        <div className="flex items-baseline gap-1.5" data-id="element-854">
                          <span className="font-semibold text-foreground" style={{
                        fontVariantNumeric: 'tabular-nums'
                      }} data-id="element-855">
                            {article.relevanceScore.toFixed(1)}
                          </span>
                          <span className="text-xs text-muted-foreground" data-id="element-856">
                            score
                          </span>
                        </div>
                        <div className="flex items-baseline gap-1.5 text-muted-foreground" data-id="element-857">
                          <span style={{
                        fontVariantNumeric: 'tabular-nums'
                      }} data-id="element-858">
                            €{(article.ave / 1000).toFixed(1)}k
                          </span>
                          <span className="text-xs" data-id="element-859">AVE</span>
                        </div>
                        <div className={`w-1.5 h-1.5 rounded-full ${getSentimentColor(article.sentiment)}`} title={sentimentLabels[article.sentiment]} data-id="element-860" />
                      </div>
                    </div>
                  </div>
                </button>
              </Fragment>)}
          </div>
        </div>

        {/* Right: unified analytics card */}
        <div className="space-y-8 md:space-y-10" data-id="element-861">
          {/* Sentiment over time */}
          <div data-id="element-862">
            <h3 className="text-sm font-semibold mb-4" data-id="element-863">Sentiment nel tempo</h3>
            <div className="h-40" data-id="element-864">
              <ResponsiveContainer width="100%" height="100%" data-id="element-865">
                <AreaChart data={sentimentOverTime} margin={{
                top: 5,
                right: 0,
                bottom: 0,
                left: -20
              }} data-id="element-866">
                  <CartesianGrid strokeDasharray="3 3" stroke="#E8E8E5" vertical={false} data-id="element-867" />
                  <XAxis dataKey="date" tick={{
                  fontSize: 10
                }} stroke="#8B8680" axisLine={false} tickLine={false} data-id="element-868" />
                  <YAxis tick={{
                  fontSize: 10
                }} stroke="#8B8680" axisLine={false} tickLine={false} data-id="element-869" />
                  <Area type="monotone" dataKey="positive" stackId="1" stroke="#4A7C59" fill="#4A7C59" fillOpacity={0.5} strokeWidth={1.5} data-id="element-870" />
                  <Area type="monotone" dataKey="neutral" stackId="1" stroke="#8B8680" fill="#8B8680" fillOpacity={0.35} strokeWidth={1.5} data-id="element-871" />
                  <Area type="monotone" dataKey="negative" stackId="1" stroke="#B85450" fill="#B85450" fillOpacity={0.5} strokeWidth={1.5} data-id="element-872" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          <Separator data-id="element-873" />

          {/* Coverage by Tier */}
          <div data-id="element-874">
            <h3 className="text-sm font-semibold mb-4" data-id="element-875">Copertura per tier</h3>
            <div className="space-y-3" data-id="element-876">
              {coverageByTier.map(item => <div key={item.tier} data-id="element-877">
                  <div className="flex items-center justify-between mb-1.5 text-sm" data-id="element-878">
                    <span className="text-foreground" data-id="element-879">{item.tier}</span>
                    <span className="text-muted-foreground" style={{
                  fontVariantNumeric: 'tabular-nums'
                }} data-id="element-880">
                      {item.count}
                    </span>
                  </div>
                  <div className="h-1 bg-muted rounded-full overflow-hidden" data-id="element-881">
                    <div className="h-full bg-foreground/80 rounded-full" style={{
                  width: `${item.percentage}%`
                }} data-id="element-882" />
                  </div>
                </div>)}
            </div>
          </div>

          <Separator data-id="element-883" />

          {/* Top Sources */}
          <div data-id="element-884">
            <h3 className="text-sm font-semibold mb-4" data-id="element-885">Top testate</h3>
            <div className="space-y-2.5" data-id="element-886">
              {topSources.map(source => <div key={source.name} className="flex items-center justify-between text-sm" data-id="element-887">
                  <span className="text-foreground truncate pr-2" data-id="element-888">
                    {source.name}
                  </span>
                  <div className="flex items-center gap-3 text-muted-foreground flex-shrink-0" data-id="element-889">
                    <span style={{
                  fontVariantNumeric: 'tabular-nums'
                }} data-id="element-890">
                      {source.articles}
                    </span>
                    <span className="text-foreground font-medium" style={{
                  fontVariantNumeric: 'tabular-nums'
                }} data-id="element-891">
                      {source.avgScore.toFixed(1)}
                    </span>
                  </div>
                </div>)}
            </div>
          </div>

          <Separator data-id="element-892" />

          {/* Spokespersons */}
          <div data-id="element-893">
            <h3 className="text-sm font-semibold mb-4" data-id="element-894">Portavoce citati</h3>
            <div className="space-y-3" data-id="element-895">
              {spokespersonMentions.map(person => <div key={person.name} className="flex items-center justify-between text-sm" data-id="element-896">
                  <div className="min-w-0 pr-2" data-id="element-897">
                    <div className="text-foreground truncate" data-id="element-898">
                      {person.name}
                    </div>
                    <div className="text-xs text-muted-foreground" data-id="element-899">
                      {person.role}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0" data-id="element-900">
                    <span className="font-medium" style={{
                  fontVariantNumeric: 'tabular-nums'
                }} data-id="element-901">
                      {person.mentions}
                    </span>
                    <div className={`w-1.5 h-1.5 rounded-full ${getSentimentColor(person.avgSentiment)}`} data-id="element-902" />
                  </div>
                </div>)}
            </div>
          </div>
        </div>
      </section>
    </div>;
}